import { projectUiActions } from '@app/core/state/ui/project/project-ui.actions';
import { createReducer, on } from '@ngrx/store';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { Loading, mapFn, mapValues, throwErrorExp } from '@core/actions/util';
import { stageActions } from '@core/actions/stage.actions';

export const ProjectLoadingStatus = {
  Loading: 'Loading',
  NotFound: 'NotFound',
  Error: 'Error',
  Loaded: 'Loaded',
} as const;
export type ProjectLoadingStatus =
  (typeof ProjectLoadingStatus)[keyof typeof ProjectLoadingStatus];

export type ProjectLoadingState =
  | {
      status: typeof ProjectLoadingStatus.Loading;
      initialStatusLoad: boolean;
      /**
       * The load started while the project's last answer was a failure or refusal (Error or NotFound).
       * Such a load never counts as a reload of the stale details still in the store (`projectLoadFail`
       * `reload`), so a transient failure cannot revive a project the server refused.
       */
      afterFailure?: boolean;
    }
  | {
      status: typeof ProjectLoadingStatus.NotFound;
      initialStatusLoad: true;
    }
  | {
      status: typeof ProjectLoadingStatus.Error;
      initialStatusLoad: boolean;
      error: string;
    }
  | {
      status: typeof ProjectLoadingStatus.Loaded;
      initialStatusLoad: true;
    };

export interface ProjectUIState {
  loadedProjectId: string | null;
  projectLoading: boolean;
  focusedFetching: boolean;
  projectSetupBoxExpanded: boolean;
  reviewerScreeningStatsStatus: Loading.State<null> | null;
  membershipScreeningStatsStatus: Loading.State<null> | null;
  projectScreeningStatsStatus: Loading.State<null> | null;
  projectLoadingState: { [projectId: string]: ProjectLoadingState };
}

const initialProjectUIState: ProjectUIState = {
  loadedProjectId: null,
  projectLoading: false,
  focusedFetching: false,
  projectSetupBoxExpanded: false,
  reviewerScreeningStatsStatus: null,
  membershipScreeningStatsStatus: null,
  projectScreeningStatsStatus: null,
  projectLoadingState: {},
};

/**
 * Sets one project's loading state, keeping every other project's (B6: it used to replace the map, so
 * a load of A erased B's state while B was loading). `projectLoading` is whether any project is loading.
 */
const withProjectLoadingStates = (
  state: ProjectUIState,
  loadingStates: { [projectId: string]: ProjectLoadingState }
): Pick<ProjectUIState, 'projectLoadingState' | 'projectLoading'> => {
  const projectLoadingState = { ...state.projectLoadingState, ...loadingStates };
  return {
    projectLoadingState,
    projectLoading: Object.values(projectLoadingState).some(
      (projectState) => projectState.status === ProjectLoadingStatus.Loading
    ),
  };
};

const withProjectLoadingState = (
  state: ProjectUIState,
  projectId: string,
  loadingState: ProjectLoadingState
): Pick<ProjectUIState, 'projectLoadingState' | 'projectLoading'> =>
  withProjectLoadingStates(state, { [projectId]: loadingState });

/** Whether a load starting now follows a failed or refused one (kept while a load is already in flight). */
const startsAfterFailure = (previous: ProjectLoadingState | undefined): boolean =>
  previous?.status === ProjectLoadingStatus.Error ||
  previous?.status === ProjectLoadingStatus.NotFound ||
  (previous?.status === ProjectLoadingStatus.Loading && previous.afterFailure === true);

export const projectUIReducer = createReducer(
  initialProjectUIState,
  on(
    projectUiActions.toggleProjectSetupBox,
    (state: ProjectUIState): ProjectUIState => ({
      ...state,
      projectSetupBoxExpanded: !state.projectSetupBoxExpanded,
    })
  ),
  // The current project changes only here (B6). Loads and their completions below never set it.
  on(
    projectUiActions.projectActivated,
    (state: ProjectUIState, props): ProjectUIState =>
      state.loadedProjectId === props.projectId
        ? state
        : { ...state, loadedProjectId: props.projectId }
  ),
  on(
    projectDetailActions.create,
    (state: ProjectUIState): ProjectUIState => ({
      ...state,
      projectLoading: true,
      projectSetupBoxExpanded: true,
    })
  ),
  on(
    projectDetailActions.loadProject,
    (state: ProjectUIState, props): ProjectUIState => ({
      ...state,
      projectSetupBoxExpanded: false,
      reviewerScreeningStatsStatus: null,
      membershipScreeningStatsStatus: null,
      projectScreeningStatsStatus: null,
      ...withProjectLoadingState(state, props.projectId, {
        status: ProjectLoadingStatus.Loading,
        initialStatusLoad:
          state.projectLoadingState[props.projectId]?.initialStatusLoad ?? false,
        ...(startsAfterFailure(state.projectLoadingState[props.projectId]) ? { afterFailure: true } : {}),
      }),
    })
  ),
  on(
    projectDetailActions.created,
    projectDetailActions.detailLoaded,
    projectDetailActions.requestToJoinProjectResponseReceivedAndGranted,
    (state: ProjectUIState, props): ProjectUIState => ({
      ...state,
      ...withProjectLoadingState(state, props.projectId, {
        status: ProjectLoadingStatus.Loaded,
        initialStatusLoad: true,
      }),
    })
  ),
  // A failed reload of a project whose details are on screen keeps it Loaded (the load says so with
  // `reload`); only a load with no details yet marks the project Error.
  on(
    projectDetailActions.projectLoadFail,
    (state: ProjectUIState, props): ProjectUIState => ({
      ...state,
      ...withProjectLoadingState(
        state,
        props.projectId,
        props.notFound
          ? { status: ProjectLoadingStatus.NotFound, initialStatusLoad: true }
          : props.reload
          ? { status: ProjectLoadingStatus.Loaded, initialStatusLoad: true }
          : {
              status: ProjectLoadingStatus.Error,
              error: props.error,
              initialStatusLoad:
                state.projectLoadingState[props.projectId]?.initialStatusLoad ?? false,
            }
      ),
    })
  ),
  on(
    projectUiActions.projectLoadTimedOut,
    (state: ProjectUIState, props): ProjectUIState => ({
      ...state,
      ...withProjectLoadingState(state, props.projectId, {
        status: ProjectLoadingStatus.Error,
        error: props.error,
        initialStatusLoad:
          state.projectLoadingState[props.projectId]?.initialStatusLoad ?? false,
      }),
    })
  ),
  on(
    projectDetailActions.retrieveProjectReviewerStats,

    (state: ProjectUIState): ProjectUIState => ({
      ...state,
      reviewerScreeningStatsStatus: Loading.loadingState(),
    })
  ),
  on(
    projectDetailActions.receivedProjectReviewerStats,
    (state: ProjectUIState, props): ProjectUIState => ({
      ...state,
      reviewerScreeningStatsStatus: Loading.successState(
        (props.entities.reviewerScreeningStats?.[props.projectId] ??
          throwErrorExp(
            'project screening stats missing from normalised action'
          ),
        null)
      ),
    })
  ),
  on(
    stageActions.receivedStageReviewerStats,
    (state: ProjectUIState, props): ProjectUIState => ({
      ...state,
      reviewerScreeningStatsStatus: mapFn(
        !!props.entities.reviewerScreeningStats?.[props.projectId],
        (retrievedScreeningStats) =>
          retrievedScreeningStats
            ? Loading.successState(
                (props.entities.reviewerScreeningStats?.[props.projectId] ??
                  throwErrorExp(
                    'project screening stats missing from normalised action'
                  ),
                null)
              )
            : state.reviewerScreeningStatsStatus
      ),
    })
  ),
  on(
    projectDetailActions.errorRetrievingProjectReviewerStats,
    (state: ProjectUIState, props): ProjectUIState => ({
      ...state,
      reviewerScreeningStatsStatus: Loading.errorState(props.error),
    })
  ),
  on(
    stageActions.errorRetrievingStageReviewerStats,
    (state: ProjectUIState, props): ProjectUIState => ({
      ...state,
      reviewerScreeningStatsStatus: !!state.reviewerScreeningStatsStatus
        ? state.reviewerScreeningStatsStatus
        : Loading.errorState(props.error),
    })
  ),
  on(
    projectDetailActions.retrieveFullProjectStats,
    (state: ProjectUIState, props): ProjectUIState => ({
      ...state,
      projectScreeningStatsStatus: Loading.loadingState(),
      membershipScreeningStatsStatus: Loading.loadingState(),
    })
  ),
  on(
    projectDetailActions.fullProjectStatsReceived,
    (state: ProjectUIState, props): ProjectUIState => {
      return {
        ...state,
        projectScreeningStatsStatus: Loading.successState(
          (props.entities.projectScreeningStats?.[props.projectId] ??
            throwErrorExp(
              'project screening stats missing from normalised action'
            ),
          null)
        ),
        // A row per membership is no longer an invariant: the server omits the screening
        // leaderboard entirely for a caller without the View screening progress graph permission,
        // and replaces membership ids with per-request surrogates when it anonymises the rows
        // (#3083). Either is a successful, complete response — throwing here took the whole
        // project page down for exactly the members the permission is meant to restrict.
        membershipScreeningStatsStatus: Loading.successState(null),
      };
    }
  ),
  on(
    projectDetailActions.errorRetrievingFullProjectStats,
    (state: ProjectUIState, props): ProjectUIState => ({
      ...state,
      projectScreeningStatsStatus: Loading.errorState(props.error),
      membershipScreeningStatsStatus: Loading.errorState(props.error),
    })
  ),
  on(
    projectDetailActions.projectListingsLoaded,
    projectDetailActions.projectListingUpdated,
    (state: ProjectUIState, props): ProjectUIState => ({
      ...state,
      // Through the same helper as every other write, so projectLoading never goes stale when a listing
      // marks a project that was loading as Loaded.
      ...withProjectLoadingStates(
        state,
        mapValues(props.entities.projects ?? {}, (): ProjectLoadingState => ({
          status: ProjectLoadingStatus.Loaded,
          initialStatusLoad: true,
        }))
      ),
    })
  )
);
